(() => {
  const RUNTIME_KEY = '__u20MobileVendingV647';
  if (window[RUNTIME_KEY]?.initAll) {
    window[RUNTIME_KEY].initAll();
    return;
  }

  const ROOT_SELECTOR = '[data-u20-vending]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SESSION_KEY = 'u20-vending-powered-v1';
  const EASTER_HANDOFF_EVENT = 'u20:vending:easter-handoff';
  const instances = new Map();

  const parseJSON = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) throw new Error(`Missing vending data: ${selector}`);
    return JSON.parse(node.textContent || '{}');
  };

  const abortError = () => {
    const error = new Error('Vending initialization aborted');
    error.name = 'AbortError';
    return error;
  };

  const preloadImage = (url, signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const image = new Image();
    image.decoding = 'async';
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      image.removeAttribute('src');
      reject(abortError());
    };
    image.onload = () => {
      cleanup();
      resolve(url);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`Unable to load ${url}`));
    };
    signal?.addEventListener('abort', onAbort, {once: true});
    image.src = url;
  });

  const sessionIsOn = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const setSessionPower = (powered) => {
    try {
      if (powered) {
        window.sessionStorage.setItem(SESSION_KEY, '1');
      } else {
        window.sessionStorage.removeItem(SESSION_KEY);
      }
    } catch (error) {
      // Storage is optional. The current page still remains fully interactive.
    }
  };

  const init = async (root) => {
    const pendingSection = root.closest('[data-u20-apparel-boundary]');
    const releasePending = () => {
      if (!pendingSection) return;
      if (pendingSection.__u20VendingPendingTimer) {
        window.clearTimeout(pendingSection.__u20VendingPendingTimer);
        delete pendingSection.__u20VendingPendingTimer;
      }
      pendingSection.classList.remove('u20-vending-pending');
    };

    if (!MOBILE_QUERY.matches) {
      releasePending();
      return;
    }
    if (root.dataset.u20VendingInitialized === 'true') return;
    root.dataset.u20VendingInitialized = 'true';
    instances.set(root, () => destroy());

    const controller = new AbortController();
    const signal = controller.signal;
    let destroy = () => {
      if (signal.aborted) return;
      const pendingVideo = root.querySelector('[data-u20-vending-video]');
      if (pendingVideo) {
        try { pendingVideo.pause(); } catch (error) {}
        pendingVideo.classList.remove('is-active');
        pendingVideo.removeAttribute('src');
        pendingVideo.load();
      }
      controller.abort();
      root.dataset.u20VendingInitialized = 'false';
      instances.delete(root);
      releasePending();
    };
    const onSectionUnload = (event) => {
      if (!event.target || !event.target.contains(root)) return;
      destroy();
    };
    document.addEventListener('shopify:section:unload', onSectionUnload, {signal});

    const section = root.closest('[data-u20-apparel-boundary]');
    const stage = root.querySelector('[data-u20-vending-stage]');
    const panorama = root.querySelector('[data-u20-vending-panorama]');
    const closeup = root.querySelector('[data-u20-vending-closeup]');
    const machine = root.querySelector('[data-u20-vending-machine]');
    const video = root.querySelector('[data-u20-vending-video]');
    const powerOnButton = root.querySelector('[data-u20-vending-power-on]');
    const openKeypadButton = root.querySelector('[data-u20-vending-open-keypad]');
    const idleImage = root.querySelector('[data-u20-vending-keypad-idle]');
    const pressedImage = root.querySelector('[data-u20-vending-keypad-pressed]');
    const productLink = root.querySelector('[data-u20-vending-product]');
    const productImages = root.querySelector('[data-u20-vending-product-images]');
    const productImageShellOne = root.querySelector('[data-u20-vending-product-image-shell="1"]');
    const productImageShellTwo = root.querySelector('[data-u20-vending-product-image-shell="2"]');
    const productImageOne = root.querySelector('[data-u20-vending-product-image="1"]');
    const productImageTwo = root.querySelector('[data-u20-vending-product-image="2"]');
    const productTitle = root.querySelector('[data-u20-vending-product-title]');
    const productPrice = root.querySelector('[data-u20-vending-product-price]');
    const screenScan = root.querySelector('[data-u20-vending-screen-scan]');
    const mysteryBox = root.querySelector('[data-u20-vending-mystery-box]');
    const status = root.querySelector('[data-u20-vending-status]');
    const flash = root.querySelector('[data-u20-vending-flash]');
    const purikuraOpen = root.querySelector('[data-u20-vending-purikura-open]');
    const purikura = root.querySelector('[data-u20-vending-purikura]');
    const purikuraClose = root.querySelector('[data-u20-vending-purikura-close]');
    const purikuraOrientationButton = root.querySelector('[data-u20-vending-purikura-orientation]');
    const purikuraVideo = root.querySelector('[data-u20-vending-purikura-video]');
    const purikuraCanvas = root.querySelector('[data-u20-vending-purikura-canvas]');
    const purikuraCountdown = root.querySelector('[data-u20-vending-purikura-countdown]');
    const purikuraStatus = root.querySelector('[data-u20-vending-purikura-status]');
    const purikuraLiveControls = root.querySelector('[data-u20-vending-purikura-live-controls]');
    const purikuraResultControls = root.querySelector('[data-u20-vending-purikura-result-controls]');
    const purikuraSnap = root.querySelector('[data-u20-vending-purikura-snap]');
    const purikuraStrength = root.querySelector('[data-u20-vending-purikura-strength]');
    const purikuraRetake = root.querySelector('[data-u20-vending-purikura-retake]');
    const purikuraSave = root.querySelector('[data-u20-vending-purikura-save]');

    if (!section || !stage || !panorama || !closeup || !machine || !video ||
      !powerOnButton || !openKeypadButton ||
      !idleImage || !pressedImage || !productLink || !productImages ||
      !productImageShellOne || !productImageShellTwo ||
      !productImageOne || !productImageTwo ||
      !productTitle || !productPrice || !screenScan || !mysteryBox || !status || !flash ||
      !purikuraOpen || !purikura || !purikuraClose || !purikuraOrientationButton ||
      !purikuraVideo || !purikuraCanvas ||
      !purikuraCountdown || !purikuraStatus || !purikuraLiveControls || !purikuraResultControls ||
      !purikuraSnap || !purikuraStrength || !purikuraRetake || !purikuraSave) {
      destroy();
      return;
    }

    let config;
    let products;
    try {
      config = parseJSON(root, '[data-u20-vending-config]');
      products = parseJSON(root, '[data-u20-vending-products]');
      if (!Array.isArray(products) || products.length === 0) throw new Error('No vending products');
    } catch (error) {
      destroy();
      return;
    }

    const easterPowerOverride = root.dataset.u20EasterPowerOverride === 'off';
    const initialPowered = easterPowerOverride ? false : sessionIsOn();
    if (easterPowerOverride) setSessionPower(false);
    root.dataset.power = initialPowered ? 'on' : 'off';
    root.dataset.busy = 'false';

    const canPlayTransitionVideo = (url) => {
      if (REDUCED_QUERY.matches) return false;
      const normalizedUrl = String(url || '').split('#')[0].split('?')[0].toLowerCase();
      const requiresAlphaHevc = normalizedUrl.endsWith('.mov');

      if (requiresAlphaHevc) {
        return Boolean(
          video.canPlayType('video/quicktime; codecs="hvc1"') ||
          video.canPlayType('video/mp4; codecs="hvc1"')
        );
      }

      return Boolean(
        video.canPlayType('video/mp4') ||
        video.canPlayType('application/vnd.apple.mpegurl')
      );
    };

    const primePowerOnVideo = () => {
      if (initialPowered || !config.powerOn || !canPlayTransitionVideo(config.powerOn)) return;
      video.preload = 'auto';
      if (video.getAttribute('src') !== config.powerOn) {
        video.src = config.powerOn;
      }
      video.load();
    };

    // This path is used by every first-time mobile visitor, so begin fetching it
    // before waiting for the rest of the machine artwork.
    primePowerOnVideo();

    let pendingEntryIntent = '';
    const capturePendingPower = () => {
      pendingEntryIntent = 'power';
      status.textContent = 'Starting vending machine.';
    };
    const capturePendingKeypad = () => {
      pendingEntryIntent = 'keypad';
      status.textContent = 'Preparing product keypad.';
    };
    powerOnButton.addEventListener('click', capturePendingPower, {signal});
    openKeypadButton.addEventListener('click', capturePendingKeypad, {signal});

    const criticalImageAssets = [
      config.background,
      config.machineOn,
      config.machineOff,
      config.keypadLogo
    ].filter(Boolean);
    const deferredImageAssets = [
      config.keypadIdle,
      config.keypadProduct,
      config.powerPressed,
      ...(Array.isArray(config.keys) ? config.keys : [])
    ].filter(Boolean);

    let preloadTimer = 0;
    try {
      await Promise.race([
        Promise.all(criticalImageAssets.map((url) => preloadImage(url, signal))),
        new Promise((resolve, reject) => {
          preloadTimer = window.setTimeout(() => reject(new Error('Vending preload timed out')), 15000);
        })
      ]);
    } catch (error) {
      powerOnButton.removeEventListener('click', capturePendingPower);
      openKeypadButton.removeEventListener('click', capturePendingKeypad);
      destroy();
      return;
    } finally {
      window.clearTimeout(preloadTimer);
    }

    if (signal.aborted || !root.isConnected) {
      destroy();
      return;
    }

    powerOnButton.removeEventListener('click', capturePendingPower);
    openKeypadButton.removeEventListener('click', capturePendingKeypad);
    deferredImageAssets.forEach((url) => preloadImage(url, signal).catch(() => {}));

    const timers = new Set();
    const productImageCache = new Map();
    const prefetchedProductPages = new Set();
    let visible = true;
    let activeVideo = false;
    let activeTransitionCancel = null;
    let selectedIndex = -1;
    let productGlitchFrame = 0;
    let productGlitchTimer = 0;
    let keypadPushTimer = 0;
    let keypadTransitionTimer = 0;
    let powerOffDelayTimer = 0;
    let exitGeneration = 0;
    let productPreloadStopped = false;
    let productSelectionToken = 0;
    let screenIdleTimer = 0;
    let screenEffectTimer = 0;
    let mysterySequence = '';
    let mysteryStartTimer = 0;
    let mysteryEndTimer = 0;
    let mysteryActive = false;
    let purikuraActive = false;
    let purikuraStream = null;
    let purikuraCountdownTimer = 0;
    let purikuraFilterTimer = 0;
    let purikuraOriginalPixels = null;
    let purikuraLuminance = null;
    let purikuraHighlightIntegral = null;
    let purikuraOrientation = 'landscape';
    const purikuraFrames = {
      landscape: {workWidth: 480, workHeight: 312, outputWidth: 960, outputHeight: 624},
      portrait: {workWidth: 360, workHeight: 480, outputWidth: 720, outputHeight: 960}
    };
    const purikuraWorkCanvas = document.createElement('canvas');
    purikuraWorkCanvas.width = 480;
    purikuraWorkCanvas.height = 312;

    const mysteryBoxReady = preloadImage(mysteryBox.currentSrc || mysteryBox.src, signal)
      .then(() => true)
      .catch(() => false);

    const syncProductTransitionRoute = () => {
      if (selectedIndex >= 0 && selectedIndex < products.length && productLink.getAttribute('href')) {
        productLink.setAttribute('data-u20-vending-tunnel', '');
        productLink.setAttribute('data-u20-no-transition', '');
        return;
      }
      productLink.removeAttribute('data-u20-vending-tunnel');
      productLink.removeAttribute('data-u20-no-transition');
    };

    const later = (callback, delay) => {
      if (signal.aborted) return 0;
      const id = window.setTimeout(() => {
        timers.delete(id);
        if (!signal.aborted) callback();
      }, delay);
      timers.add(id);
      return id;
    };

    const cancelScreenIdleEffect = () => {
      if (screenIdleTimer) {
        window.clearTimeout(screenIdleTimer);
        timers.delete(screenIdleTimer);
        screenIdleTimer = 0;
      }
      if (screenEffectTimer) {
        window.clearTimeout(screenEffectTimer);
        timers.delete(screenEffectTimer);
        screenEffectTimer = 0;
      }
      closeup.classList.remove('is-screen-scanning');
    };

    const scheduleScreenIdleEffect = () => {
      if (REDUCED_QUERY.matches || productPreloadStopped || document.hidden || !visible || closeup.hidden || purikuraActive) return;
      if (screenIdleTimer || screenEffectTimer) return;
      screenIdleTimer = later(() => {
        screenIdleTimer = 0;
        if (document.hidden || !visible || closeup.hidden || root.dataset.busy === 'true' || purikuraActive) {
          scheduleScreenIdleEffect();
          return;
        }
        closeup.classList.add('is-screen-scanning');
        screenEffectTimer = later(() => {
          screenEffectTimer = 0;
          closeup.classList.remove('is-screen-scanning');
          scheduleScreenIdleEffect();
        }, 920);
      }, 2000);
    };

    const resetScreenIdleEffect = () => {
      cancelScreenIdleEffect();
      scheduleScreenIdleEffect();
    };

    const clearPurikuraTimer = () => {
      if (purikuraCountdownTimer) {
        window.clearTimeout(purikuraCountdownTimer);
        timers.delete(purikuraCountdownTimer);
        purikuraCountdownTimer = 0;
      }
      if (purikuraFilterTimer) {
        window.clearTimeout(purikuraFilterTimer);
        timers.delete(purikuraFilterTimer);
        purikuraFilterTimer = 0;
      }
    };

    const syncPurikuraOrientation = () => {
      const isPortrait = purikuraOrientation === 'portrait';
      purikura.classList.toggle('is-portrait', isPortrait);
      purikura.dataset.photoOrientation = purikuraOrientation;
      purikuraOrientationButton.setAttribute('aria-pressed', String(isPortrait));
      purikuraOrientationButton.setAttribute(
        'aria-label',
        isPortrait ? 'Switch to landscape photo' : 'Switch to portrait photo'
      );
    };

    const setPurikuraOrientation = (orientation) => {
      purikuraOrientation = orientation === 'portrait' ? 'portrait' : 'landscape';
      syncPurikuraOrientation();
    };

    const releaseActiveVendingControl = () => {
      const activeControl = document.activeElement;
      if (!activeControl || !root.contains(activeControl) || typeof activeControl.blur !== 'function') return;
      activeControl.blur();
    };

    const pageHasOpenScrollOwner = () => {
      const knownOwner = document.querySelector([
        '.drawer.is-open',
        'header-search-popdown details[open]',
        '.modal__overlay--password.is-open',
        '.product-quick-add.is-open',
        '[data-u20-mobile-drawer-cloud]:not([hidden])'
      ].join(','));
      if (knownOwner) return true;
      return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
        .some((dialog) => {
          if (dialog.hidden || dialog.getAttribute('aria-hidden') === 'true') return false;
          if (dialog.closest('[hidden], [aria-hidden="true"], details:not([open])')) return false;
          const style = window.getComputedStyle(dialog);
          return style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            style.pointerEvents !== 'none' &&
            dialog.getClientRects().length > 0;
        });
    };

    const recoverPageScroll = (attempt = 0) => {
      if (signal.aborted || pageHasOpenScrollOwner()) return;
      const htmlLocked = document.documentElement.hasAttribute('data-scroll-locked');
      const bodyFixed = document.body.style.position === 'fixed';
      const bodyHidden = document.body.style.overflow === 'hidden';
      if (!htmlLocked && !bodyFixed && !bodyHidden) return;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      if (attempt < 2) later(() => recoverPageScroll(attempt + 1), 40);
    };

    const schedulePageScrollRecovery = (reason = 'vending-panorama') => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (window.U20MobileScrollRecovery?.recover) {
            window.U20MobileScrollRecovery.recover(reason, {forceThemeUnlock: true});
            return;
          }
          recoverPageScroll();
        });
      });
    };

    const cancelKeypadTransition = () => {
      if (keypadPushTimer) {
        window.clearTimeout(keypadPushTimer);
        timers.delete(keypadPushTimer);
        keypadPushTimer = 0;
      }
      if (keypadTransitionTimer) {
        window.clearTimeout(keypadTransitionTimer);
        timers.delete(keypadTransitionTimer);
        keypadTransitionTimer = 0;
      }
      root.classList.remove('is-keypad-pushing', 'is-keypad-transitioning');
    };

    const cancelPendingPowerOff = () => {
      exitGeneration += 1;
      if (!powerOffDelayTimer) return;
      window.clearTimeout(powerOffDelayTimer);
      timers.delete(powerOffDelayTimer);
      powerOffDelayTimer = 0;
    };

    const stopPurikuraCamera = () => {
      purikura.classList.remove('is-live-preview');
      if (purikuraStream) {
        purikuraStream.getTracks().forEach((track) => track.stop());
        purikuraStream = null;
      }
      purikuraVideo.pause();
      purikuraVideo.srcObject = null;
    };

    const resetPurikuraBuffers = () => {
      purikuraOriginalPixels = null;
      purikuraLuminance = null;
      purikuraHighlightIntegral = null;
      purikuraWorkCanvas.width = 1;
      purikuraWorkCanvas.height = 1;
      purikuraCanvas.width = 1;
      purikuraCanvas.height = 1;
    };

    const closePurikura = () => {
      if (!purikuraActive) return;
      purikuraActive = false;
      releaseActiveVendingControl();
      clearPurikuraTimer();
      stopPurikuraCamera();
      resetPurikuraBuffers();
      purikura.hidden = true;
      purikura.setAttribute('aria-hidden', 'true');
      purikuraVideo.hidden = false;
      purikuraCanvas.hidden = true;
      purikuraCountdown.textContent = '';
      purikuraCountdown.classList.remove('is-save-complete');
      purikuraLiveControls.hidden = false;
      purikuraResultControls.hidden = true;
      purikuraSnap.disabled = false;
      purikuraSave.disabled = false;
      purikuraOrientationButton.disabled = false;
      setPurikuraOrientation('landscape');
      root.dataset.busy = 'false';
      announce('Select a product from one to twelve, or open Purikura.');
      resetScreenIdleEffect();
    };

    const startPurikuraCamera = async () => {
      if (!purikuraActive) return false;
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        purikuraStatus.textContent = 'CAMERA NOT SUPPORTED IN THIS BROWSER';
        return false;
      }

      stopPurikuraCamera();
      purikuraStatus.textContent = 'REQUESTING CAMERA PERMISSION...';
      try {
        purikuraStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: {ideal: 'user'},
            width: {ideal: 720},
            height: {ideal: 960}
          }
        });
        if (!purikuraActive) {
          stopPurikuraCamera();
          return false;
        }
        purikuraVideo.srcObject = purikuraStream;
        purikuraVideo.classList.add('is-user-facing');
        purikuraVideo.hidden = false;
        purikuraCanvas.hidden = true;
        purikuraLiveControls.hidden = false;
        purikuraResultControls.hidden = true;
        await purikuraVideo.play();
        purikura.classList.add('is-live-preview');
        purikuraOrientationButton.disabled = false;
        purikuraStatus.textContent = 'No photos are stored or uploaded.';
        return true;
      } catch (error) {
        stopPurikuraCamera();
        purikuraStatus.textContent = error && error.name === 'NotAllowedError'
          ? 'CAMERA PERMISSION DECLINED'
          : 'CAMERA COULD NOT START';
        return false;
      }
    };

    const renderPurikuraFilter = () => {
      if (!purikuraOriginalPixels) return;
      const width = purikuraOriginalPixels.width;
      const height = purikuraOriginalPixels.height;
      purikuraWorkCanvas.width = width;
      purikuraWorkCanvas.height = height;
      const workContext = purikuraWorkCanvas.getContext('2d', {willReadFrequently: true});
      const outputContext = purikuraCanvas.getContext('2d', {alpha: false});
      if (!workContext || !outputContext) return;

      const rawStrength = Math.max(0, Math.min(1, Number(purikuraStrength.value || 80) / 100));
      const strength = Math.pow(rawStrength, 0.58);
      const extreme = Math.pow(rawStrength, 1.8);
      const source = purikuraOriginalPixels.data;
      const filtered = new ImageData(new Uint8ClampedArray(source), width, height);
      const pixels = filtered.data;
      const pixelCount = width * height;
      const integralWidth = width + 1;

      if (!purikuraLuminance || purikuraLuminance.length !== pixelCount) {
        purikuraLuminance = new Float32Array(pixelCount);
        purikuraHighlightIntegral = new Float32Array((width + 1) * (height + 1));
        for (let y = 0; y < height; y += 1) {
          let rowHighlights = 0;
          for (let x = 0; x < width; x += 1) {
            const pixelIndex = (y * width) + x;
            const sourceIndex = pixelIndex * 4;
            const luminance = (source[sourceIndex] * 0.299) +
              (source[sourceIndex + 1] * 0.587) +
              (source[sourceIndex + 2] * 0.114);
            purikuraLuminance[pixelIndex] = luminance;
            rowHighlights += Math.max(0, (luminance - 172) / 83);
            purikuraHighlightIntegral[((y + 1) * integralWidth) + x + 1] =
              purikuraHighlightIntegral[(y * integralWidth) + x + 1] + rowHighlights;
          }
        }
      }

      const centerX = Math.max(1, (width - 1) / 2);
      const centerY = Math.max(1, (height - 1) / 2);
      const contrast = 1 + (strength * 0.32) + (extreme * 0.18);
      const saturation = 1 + (strength * 0.22) + (extreme * 0.18);
      const highlightRadius = 4 + Math.round(extreme * 4);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixelIndex = (y * width) + x;
          const index = pixelIndex * 4;
          const normalizedLuminance = purikuraLuminance[pixelIndex] / 255;
          const shadow = Math.pow(1 - normalizedLuminance, 1.7);
          const midtone = Math.max(0, 1 - (Math.abs(normalizedLuminance - 0.52) / 0.52));
          const nx = (x - centerX) / centerX;
          const ny = (y - centerY) / centerY;
          const radiusSquared = (nx * nx) + (ny * ny);
          const chromaOffset = radiusSquared > 0.38 && strength > 0.38
            ? Math.round(extreme * 2) + 1
            : 0;
          const redIndex = ((y * width) + Math.max(0, x - chromaOffset)) * 4;
          const blueIndex = ((y * width) + Math.min(width - 1, x + chromaOffset)) * 4;
          const left = ((y * width) + Math.max(0, x - 1)) * 4;
          const right = ((y * width) + Math.min(width - 1, x + 1)) * 4;
          const above = ((Math.max(0, y - 1) * width) + x) * 4;
          const below = ((Math.min(height - 1, y + 1) * width) + x) * 4;
          const averageRed = (source[left] + source[right] + source[above] + source[below]) * 0.25;
          const averageGreen = (source[left + 1] + source[right + 1] + source[above + 1] + source[below + 1]) * 0.25;
          const averageBlue = (source[left + 2] + source[right + 2] + source[above + 2] + source[below + 2]) * 0.25;

          const directFlash = Math.max(0, 1 - (radiusSquared * 0.72));
          const flashLift = ((8 * strength) + (22 * extreme)) * directFlash;
          let red = ((source[redIndex] - 118) * contrast) + 118 + flashLift + (6 * strength);
          let green = ((source[index + 1] - 118) * contrast) + 118 + (flashLift * 0.92) + (2 * strength);
          let blue = ((source[blueIndex + 2] - 118) * contrast) + 118 + (flashLift * 0.72) - (2 * strength);

          red = purikuraLuminance[pixelIndex] + ((red - purikuraLuminance[pixelIndex]) * saturation);
          green = purikuraLuminance[pixelIndex] + ((green - purikuraLuminance[pixelIndex]) * saturation);
          blue = purikuraLuminance[pixelIndex] + ((blue - purikuraLuminance[pixelIndex]) * saturation);

          red += ((18 * strength) + (12 * extreme)) * midtone;
          green += ((4 * strength) + (2 * extreme)) * midtone;
          blue -= ((10 * strength) + (6 * extreme)) * midtone;
          red += (source[index] - averageRed) * ((0.38 * strength) + (0.2 * extreme));
          green += (source[index + 1] - averageGreen) * ((0.32 * strength) + (0.16 * extreme));
          blue += (source[index + 2] - averageBlue) * ((0.35 * strength) + (0.18 * extreme));
          const blackCrush = ((10 * strength) + (18 * extreme)) * shadow * shadow;
          red -= blackCrush + (3 * shadow * extreme);
          green += (3 * shadow * strength) - blackCrush;
          blue += (10 * shadow * strength) + (5 * shadow * extreme) - blackCrush;

          const shoulder = Math.max(0, (normalizedLuminance - 0.62) / 0.38);
          red += shoulder * shoulder * ((48 * strength) + (30 * extreme));
          green += shoulder * shoulder * ((38 * strength) + (24 * extreme));
          blue += shoulder * shoulder * ((20 * strength) + (15 * extreme));

          const x0 = Math.max(0, x - highlightRadius);
          const x1 = Math.min(width, x + highlightRadius + 1);
          const y0 = Math.max(0, y - highlightRadius);
          const y1 = Math.min(height, y + highlightRadius + 1);
          const highlightSum =
            purikuraHighlightIntegral[(y1 * integralWidth) + x1] -
            purikuraHighlightIntegral[(y0 * integralWidth) + x1] -
            purikuraHighlightIntegral[(y1 * integralWidth) + x0] +
            purikuraHighlightIntegral[(y0 * integralWidth) + x0];
          const bloom = highlightSum / ((x1 - x0) * (y1 - y0));
          red += bloom * ((34 * strength) + (24 * extreme));
          green += bloom * ((29 * strength) + (20 * extreme));
          blue += bloom * ((18 * strength) + (14 * extreme));

          const grainHash = Math.imul(x + 1, 1597334677) ^ Math.imul(y + 1, 3812015801);
          const chromaHash = Math.imul((x >> 1) + 17, 1103515245) ^ Math.imul((y >> 1) + 29, 12345);
          const grain = (((grainHash >>> 8) & 1023) / 511.5) - 1;
          const chromaNoise = (((chromaHash >>> 9) & 1023) / 511.5) - 1;
          const grainAmount = ((2.5 + (12 * shadow)) * strength) + (6 * extreme);
          red += (grain * grainAmount) + (chromaNoise * shadow * ((9 * strength) + (6 * extreme)));
          green += (grain * grainAmount * 0.7) - (chromaNoise * shadow * ((5 * strength) + (4 * extreme)));
          blue += (grain * grainAmount * 1.12) + (chromaNoise * shadow * ((14 * strength) + (9 * extreme)));
          if (rawStrength > 0.55 && ((grainHash >>> 17) & 8191) === 0) {
            red += 75 * extreme;
            green -= 18 * extreme;
            blue += 58 * extreme;
          }

          const edgeSoftness = Math.max(0, radiusSquared - 0.35) * ((0.14 * strength) + (0.08 * extreme));
          red = (red * (1 - edgeSoftness)) + (averageRed * edgeSoftness);
          green = (green * (1 - edgeSoftness)) + (averageGreen * edgeSoftness);
          blue = (blue * (1 - edgeSoftness)) + (averageBlue * edgeSoftness);
          const vignette = Math.max(0, radiusSquared - 0.18) * ((20 * strength) + (18 * extreme));

          pixels[index] = Math.max(0, Math.min(255, Math.round(red - vignette)));
          pixels[index + 1] = Math.max(0, Math.min(255, Math.round(green - vignette)));
          pixels[index + 2] = Math.max(0, Math.min(255, Math.round(blue - vignette)));
        }
      }

      workContext.putImageData(filtered, 0, 0);
      const frame = height > width ? purikuraFrames.portrait : purikuraFrames.landscape;
      const {outputWidth, outputHeight} = frame;
      purikuraCanvas.width = outputWidth;
      purikuraCanvas.height = outputHeight;
      outputContext.imageSmoothingEnabled = true;
      outputContext.imageSmoothingQuality = 'medium';
      outputContext.fillStyle = '#06151c';
      outputContext.fillRect(0, 0, outputWidth, outputHeight);
      outputContext.drawImage(purikuraWorkCanvas, 0, 0, outputWidth, outputHeight);
    };

    const capturePurikuraFrame = () => {
      if (!purikuraStream || purikuraVideo.readyState < 2) {
        purikuraStatus.textContent = 'CAMERA IS NOT READY';
        purikuraSnap.disabled = false;
        purikuraOrientationButton.disabled = false;
        return;
      }
      const frame = purikuraFrames[purikuraOrientation];
      const width = frame.workWidth;
      const height = frame.workHeight;
      purikuraWorkCanvas.width = width;
      purikuraWorkCanvas.height = height;
      const context = purikuraWorkCanvas.getContext('2d', {willReadFrequently: true});
      if (!context) return;
      const videoWidth = purikuraVideo.videoWidth || 720;
      const videoHeight = purikuraVideo.videoHeight || 960;
      const targetRatio = width / height;
      const sourceRatio = videoWidth / videoHeight;
      let sourceX = 0;
      let sourceY = 0;
      let sourceWidth = videoWidth;
      let sourceHeight = videoHeight;
      if (sourceRatio > targetRatio) {
        sourceWidth = videoHeight * targetRatio;
        sourceX = (videoWidth - sourceWidth) / 2;
      } else {
        sourceHeight = videoWidth / targetRatio;
        sourceY = (videoHeight - sourceHeight) / 2;
      }
      context.save();
      context.translate(width, 0);
      context.scale(-1, 1);
      context.drawImage(
        purikuraVideo,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        width,
        height
      );
      context.restore();
      purikuraOriginalPixels = context.getImageData(0, 0, width, height);
      purikuraLuminance = null;
      purikuraHighlightIntegral = null;
      stopPurikuraCamera();
      renderPurikuraFilter();
      purikuraVideo.hidden = true;
      purikuraCanvas.hidden = false;
      purikuraLiveControls.hidden = true;
      purikuraResultControls.hidden = false;
      purikuraSnap.disabled = false;
      purikuraOrientationButton.disabled = true;
      purikuraStatus.textContent = 'No photos are stored or uploaded.';
    };

    const beginPurikuraCountdown = () => {
      if (!purikuraStream || purikuraSnap.disabled) return;
      clearPurikuraTimer();
      purikuraCountdown.classList.remove('is-save-complete');
      purikuraSnap.disabled = true;
      purikuraOrientationButton.disabled = true;
      if (REDUCED_QUERY.matches) {
        capturePurikuraFrame();
        return;
      }
      let count = 3;
      const tick = () => {
        purikuraCountdownTimer = 0;
        purikuraCountdown.textContent = String(count);
        if (count === 0) {
          purikuraCountdown.textContent = '';
          capturePurikuraFrame();
          return;
        }
        count -= 1;
        purikuraCountdownTimer = later(tick, 560);
      };
      tick();
    };

    const openPurikura = async () => {
      if (purikuraActive || root.dataset.busy === 'true' || mysteryActive) return;
      purikuraActive = true;
      root.dataset.busy = 'true';
      cancelScreenIdleEffect();
      purikura.hidden = false;
      purikura.setAttribute('aria-hidden', 'false');
      setPurikuraOrientation('landscape');
      purikuraOrientationButton.disabled = false;
      purikuraStatus.textContent = 'REQUESTING CAMERA PERMISSION...';
      announce('Purikura camera opened. Photos stay on this device.');
      await startPurikuraCamera();
    };

    const showPurikuraSaveComplete = () => {
      if (!purikuraActive) return;
      purikuraStatus.textContent = 'No photos are stored or uploaded.';
      purikuraCountdown.classList.add('is-save-complete');
      purikuraCountdown.textContent = 'SAVE-COMPLETE';
      purikuraCountdownTimer = later(async () => {
        purikuraCountdownTimer = 0;
        if (!purikuraActive) return;
        purikuraCountdown.textContent = '';
        purikuraCountdown.classList.remove('is-save-complete');
        resetPurikuraBuffers();
        purikuraCanvas.hidden = true;
        purikuraVideo.hidden = false;
        purikuraResultControls.hidden = true;
        purikuraLiveControls.hidden = false;
        purikuraSave.disabled = false;
        await startPurikuraCamera();
      }, 900);
    };

    const createPurikuraExportCanvas = async () => {
      if (document.fonts && document.fonts.load) {
        try {
          await document.fonts.load('32px "fangzheng"', 'UnDeR20');
        } catch (error) {
          // The canvas fallback font still keeps the saved photo available.
        }
      }

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = purikuraCanvas.width;
      exportCanvas.height = purikuraCanvas.height;
      const exportContext = exportCanvas.getContext('2d', {alpha: false});
      if (!exportContext) return purikuraCanvas;

      exportContext.drawImage(purikuraCanvas, 0, 0);
      exportContext.fillStyle = '#ffffff';
      exportContext.font = '32px "fangzheng", "Courier New", monospace';
      exportContext.textAlign = 'right';
      exportContext.textBaseline = 'bottom';
      exportContext.fillText('UnDeR20', exportCanvas.width - 30, exportCanvas.height - 22);
      return exportCanvas;
    };

    const savePurikura = async () => {
      if (!purikuraOriginalPixels) return;
      purikuraSave.disabled = true;
      purikuraStatus.textContent = 'PREPARING PHOTO...';
      const exportCanvas = await createPurikuraExportCanvas();
      exportCanvas.toBlob(async (blob) => {
        if (!blob || !purikuraActive) {
          purikuraStatus.textContent = 'PHOTO COULD NOT BE SAVED';
          purikuraSave.disabled = false;
          return;
        }
        const file = new File([blob], `under20-purikura-${Date.now()}.png`, {type: 'image/png'});
        try {
          if (navigator.share && navigator.canShare && navigator.canShare({files: [file]})) {
            await navigator.share({files: [file], title: 'Under20 Purikura'});
            showPurikuraSaveComplete();
            return;
          }
        } catch (error) {
          if (error && error.name === 'AbortError') {
            purikuraStatus.textContent = 'SAVE CANCELLED';
            purikuraSave.disabled = false;
            return;
          }
        }
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        showPurikuraSaveComplete();
      }, 'image/png');
    };

    const retryProductImageUrl = (url) => {
      try {
        const retryUrl = new URL(url, window.location.href);
        retryUrl.searchParams.set('_u20_retry', '1');
        return retryUrl.href;
      } catch (error) {
        return url;
      }
    };

    const loadProductImage = (url, priority = 'low') => {
      if (!url || productPreloadStopped) return Promise.resolve('');

      const cached = productImageCache.get(url);
      if (cached) return cached.promise;

      const record = {
        image: null,
        promise: null,
        resolvedUrl: '',
        status: 'loading'
      };

      record.promise = new Promise((resolve) => {
        let attempt = 0;

        const loadAttempt = () => {
          if (productPreloadStopped) {
            record.status = 'stopped';
            resolve('');
            return;
          }

          const requestUrl = attempt === 0 ? url : retryProductImageUrl(url);
          const image = new Image();
          record.image = image;
          image.decoding = 'async';
          if ('fetchPriority' in image) image.fetchPriority = priority;

          const clearHandlers = () => {
            image.onload = null;
            image.onerror = null;
          };

          image.onload = () => {
            clearHandlers();
            const decodePromise = typeof image.decode === 'function'
              ? image.decode().catch(() => {})
              : Promise.resolve();
            decodePromise.finally(() => {
              record.status = 'loaded';
              record.resolvedUrl = requestUrl;
              resolve(requestUrl);
            });
          };

          image.onerror = () => {
            clearHandlers();
            if (attempt === 0 && !productPreloadStopped) {
              attempt = 1;
              later(loadAttempt, 140);
              return;
            }
            record.status = 'error';
            productImageCache.delete(url);
            resolve('');
          };

          image.src = requestUrl;
        };

        loadAttempt();
      });

      productImageCache.set(url, record);
      return record.promise;
    };

    const prefetchProductPage = (url) => {
      if (!url || prefetchedProductPages.has(url)) return;
      prefetchedProductPages.add(url);
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url;
      document.head.appendChild(hint);
    };

    const preloadProductAt = (index, priority = 'low') => {
      const product = products[index];
      if (!product || productPreloadStopped) return Promise.resolve([]);
      const urls = [product.image1, product.image2].filter(Boolean);
      return Promise.all(urls.map((url) => loadProductImage(url, priority)));
    };

    const preloadNextProducts = (index, count = 2) => {
      if (productPreloadStopped) return;
      const pending = [];
      for (let offset = 1; offset <= count; offset += 1) {
        const nextIndex = index + offset;
        if (nextIndex >= products.length) break;
        pending.push(preloadProductAt(nextIndex, 'low'));
      }
      Promise.all(pending).catch(() => {});
    };

    const isBottomProduct = (product) => {
      const collectionHandles = Array.isArray(product.collections) ? product.collections : [];
      const classification = [product.type, ...collectionHandles]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return /(^|[-_\s])(bottoms?|pants?|trousers?|jeans?|shorts?|skirts?|culottes?|leggings?)([-_\s]|$)/.test(classification);
    };

    const isTopProduct = (product) => {
      const collectionHandles = Array.isArray(product.collections) ? product.collections : [];
      const classification = [product.type, ...collectionHandles]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return /(^|[-_\s])tops?([-_\s]|$)/.test(classification);
    };

    const artworkBoundsCache = new Map();
    let activeTopArtwork = [];

    const measureArtworkBounds = (url) => {
      if (!url) return Promise.resolve(null);
      if (artworkBoundsCache.has(url)) return artworkBoundsCache.get(url);

      const pending = new Promise((resolve) => {
        const source = new Image();
        source.crossOrigin = 'anonymous';
        source.decoding = 'async';
        source.onload = () => {
          const naturalWidth = source.naturalWidth || 1;
          const naturalHeight = source.naturalHeight || 1;
          const sampleScale = Math.min(1, 256 / Math.max(naturalWidth, naturalHeight));
          const width = Math.max(1, Math.round(naturalWidth * sampleScale));
          const height = Math.max(1, Math.round(naturalHeight * sampleScale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', {willReadFrequently: true});
          if (!context) {
            resolve(null);
            return;
          }
          try {
            context.drawImage(source, 0, 0, width, height);
            const pixels = context.getImageData(0, 0, width, height).data;
            let left = width;
            let top = height;
            let right = -1;
            let bottom = -1;
            for (let y = 0; y < height; y += 1) {
              for (let x = 0; x < width; x += 1) {
                if (pixels[(y * width + x) * 4 + 3] < 12) continue;
                if (x < left) left = x;
                if (x > right) right = x;
                if (y < top) top = y;
                if (y > bottom) bottom = y;
              }
            }
            if (right < left || bottom < top) {
              resolve(null);
              return;
            }
            resolve({
              naturalWidth,
              naturalHeight,
              left: left / sampleScale,
              top: top / sampleScale,
              width: (right - left + 1) / sampleScale,
              height: (bottom - top + 1) / sampleScale
            });
          } catch (error) {
            resolve(null);
          }
        };
        source.onerror = () => resolve(null);
        source.src = url;
      });

      artworkBoundsCache.set(url, pending);
      return pending;
    };

    const clearTopImageFit = () => {
      [productImageOne, productImageTwo].forEach((image) => {
        image.style.removeProperty('--u20-vending-top-scale');
        image.style.removeProperty('--u20-vending-top-x');
        image.style.removeProperty('--u20-vending-top-y');
      });
      activeTopArtwork = [];
    };

    const fitTopImage = (image, shell, bounds) => {
      if (!bounds || shell.hidden) return;
      const shellWidth = shell.clientWidth;
      const shellHeight = shell.clientHeight;
      if (!shellWidth || !shellHeight) return;
      const baseScale = Math.min(
        shellWidth / bounds.naturalWidth,
        shellHeight / bounds.naturalHeight
      );
      const safeInset = Math.max(5, shellWidth * 0.025);
      const multiplier = Math.min(
        2.4,
        (shellWidth - safeInset * 2) / (bounds.width * baseScale),
        (shellHeight - safeInset * 2) / (bounds.height * baseScale)
      );
      const artworkCenterX = bounds.left + bounds.width / 2;
      const artworkCenterY = bounds.top + bounds.height / 2;
      const offsetX = (artworkCenterX - bounds.naturalWidth / 2) * baseScale * multiplier;
      const offsetY = (artworkCenterY - bounds.naturalHeight / 2) * baseScale * multiplier;
      image.style.setProperty('--u20-vending-top-scale', multiplier.toFixed(4));
      image.style.setProperty('--u20-vending-top-x', `${(-offsetX).toFixed(2)}px`);
      image.style.setProperty('--u20-vending-top-y', `${(-offsetY).toFixed(2)}px`);
    };

    const fitActiveTopImages = () => {
      if (!productImages.classList.contains('is-tops')) return;
      fitTopImage(productImageOne, productImageShellOne, activeTopArtwork[0]);
      fitTopImage(productImageTwo, productImageShellTwo, activeTopArtwork[1]);
    };

    window.addEventListener('resize', fitActiveTopImages, {signal, passive: true});

    const stopProductGlitch = () => {
      if (productGlitchFrame) {
        window.cancelAnimationFrame(productGlitchFrame);
        productGlitchFrame = 0;
      }
      if (productGlitchTimer) {
        window.clearTimeout(productGlitchTimer);
        timers.delete(productGlitchTimer);
        productGlitchTimer = 0;
      }
      productImages.classList.remove('is-glitching');
    };

    const triggerProductGlitch = () => {
      stopProductGlitch();
      if (REDUCED_QUERY.matches) return;
      productGlitchFrame = window.requestAnimationFrame(() => {
        productGlitchFrame = 0;
        productImages.classList.add('is-glitching');
        productGlitchTimer = later(() => {
          productGlitchTimer = 0;
          productImages.classList.remove('is-glitching');
        }, 800);
      });
    };

    const finishKeypadTransition = () => {
      cancelKeypadTransition();
      panorama.hidden = true;
      closeup.hidden = false;
      root.dataset.busy = 'false';
      later(() => {
        closeup.classList.add('is-idle');
        resetScreenIdleEffect();
      }, 600);
    };

    const announce = (message) => {
      status.textContent = message;
    };

    const clearMysteryTimers = () => {
      [mysteryStartTimer, mysteryEndTimer].forEach((id) => {
        if (!id) return;
        window.clearTimeout(id);
        timers.delete(id);
      });
      mysteryStartTimer = 0;
      mysteryEndTimer = 0;
    };

    const clearSelectedProduct = () => {
      productSelectionToken += 1;
      selectedIndex = -1;
      closeup.classList.remove('has-product');
      productLink.classList.remove('is-visible');
      productLink.removeAttribute('href');
      productImageOne.removeAttribute('src');
      productImageTwo.removeAttribute('src');
      productImageShellOne.hidden = true;
      productImageShellTwo.hidden = true;
      productImageShellOne.style.removeProperty('--u20-vending-product-glitch-image');
      productImageShellTwo.style.removeProperty('--u20-vending-product-glitch-image');
      productTitle.textContent = '';
      productPrice.textContent = '';
      stopProductGlitch();
      syncProductTransitionRoute();
    };

    const resetMysteryEffect = (restorePrompt = true) => {
      clearMysteryTimers();
      mysteryActive = false;
      mysterySequence = '';
      closeup.classList.remove('is-mystery');
      closeup.classList.remove('is-mystery-dispensing');

      if (!restorePrompt || closeup.hidden || root.dataset.power !== 'on') return;
      clearSelectedProduct();
      idleImage.src = config.keypadIdle;
      closeup.classList.add('is-idle');
      root.dataset.busy = 'false';
      announce('Select a product from one to twelve.');
      resetScreenIdleEffect();
    };

    const triggerMysteryEffect = () => {
      if (mysteryActive || closeup.hidden || root.dataset.power !== 'on') return;

      mysteryActive = true;
      root.dataset.busy = 'true';
      cancelScreenIdleEffect();
      clearSelectedProduct();
      idleImage.src = config.keypadProduct;
      closeup.classList.add('is-idle');
      closeup.classList.add('is-mystery');
      announce('Mystery delivery activated.');

      mysteryBoxReady.then((ready) => {
        if (!mysteryActive) return;
        if (!ready) {
          resetMysteryEffect(true);
          return;
        }

        const questionSequenceDuration = REDUCED_QUERY.matches ? 260 : 1100;
        mysteryStartTimer = later(() => {
          mysteryStartTimer = 0;
          if (!mysteryActive || document.hidden || !visible || closeup.hidden) {
            resetMysteryEffect(true);
            return;
          }
          closeup.classList.add('is-mystery-dispensing');
          mysteryEndTimer = later(() => {
            mysteryEndTimer = 0;
            resetMysteryEffect(true);
          }, 1400);
        }, questionSequenceDuration);
      });
    };

    const recordMysteryKey = (keyNumber) => {
      const digit = keyNumber === 10 ? '0' : String(keyNumber);
      const candidate = `${mysterySequence}${digit}`;
      if ('2020'.startsWith(candidate)) {
        mysterySequence = candidate;
      } else {
        mysterySequence = digit === '2' ? '2' : '';
      }

      if (mysterySequence !== '2020') return false;
      mysterySequence = '';
      return true;
    };

    const setPower = (powered) => {
      if (!powered && purikuraActive) closePurikura();
      if (powered) {
        delete root.dataset.u20EasterPowerOverride;
        delete root.dataset.u20EasterHandoff;
      }
      root.classList.remove('is-powering-off');
      root.dataset.power = powered ? 'on' : 'off';
      root.dataset.busy = 'false';
      machine.src = powered ? config.machineOn : config.machineOff;
      machine.alt = powered ? 'Illuminated Under20 vending machine' : 'Under20 vending machine';
      announce(powered ? 'Vending machine ready. Tap the keypad.' : 'Vending machine off. Tap the screen to start.');
    };

    const finishVideo = (powered, resolve) => {
      activeVideo = false;
      try { video.pause(); } catch (error) {}
      video.classList.remove('is-active');
      video.removeAttribute('src');
      video.load();
      setPower(powered);
      setSessionPower(powered);
      resolve();
    };

    const playTransition = (url, powered) => new Promise((resolve) => {
      root.dataset.busy = 'true';
      if (!url || !canPlayTransitionVideo(url)) {
        setPower(powered);
        setSessionPower(powered);
        resolve();
        return;
      }

      let settled = false;
      let loadTimer = 0;
      let playbackTimer = 0;
      const clearTrackedTimer = (id) => {
        if (!id) return;
        window.clearTimeout(id);
        timers.delete(id);
      };
      const removeMediaListeners = () => {
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('canplay', tryPlay);
        video.removeEventListener('ended', complete);
        video.removeEventListener('error', complete);
      };
      const complete = () => {
        if (settled) return;
        settled = true;
        clearTrackedTimer(loadTimer);
        clearTrackedTimer(playbackTimer);
        removeMediaListeners();
        if (activeTransitionCancel === complete) activeTransitionCancel = null;
        finishVideo(powered, resolve);
      };

      const onPlaying = () => {
        clearTrackedTimer(loadTimer);
        loadTimer = 0;
        const mediaDuration = Number.isFinite(video.duration) && video.duration > 0
          ? Math.ceil(video.duration * 1000) + 1200
          : (powered ? 4500 : 2800);
        playbackTimer = later(complete, mediaDuration);
      };

      const tryPlay = () => {
        if (settled || !video.paused) return;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // A slow connection can reject play() before enough data exists.
            // The canplay listener below retries; the load watchdog remains the fallback.
          });
        }
      };

      activeVideo = true;
      activeTransitionCancel = complete;
      video.preload = 'auto';
      if (video.getAttribute('src') !== url) {
        video.src = url;
        video.load();
      }
      try {
        video.currentTime = 0;
      } catch (error) {
        // The media timeline may not be available yet on a cold, slow connection.
      }
      video.classList.add('is-active');
      video.addEventListener('playing', onPlaying, {once: true});
      video.addEventListener('canplay', tryPlay, {once: true});
      video.addEventListener('ended', complete, {once: true});
      video.addEventListener('error', complete, {once: true});
      loadTimer = later(complete, powered ? 12000 : 6000);
      tryPlay();
    });

    const powerOn = async () => {
      if (root.dataset.power !== 'off' || root.dataset.busy === 'true') return;
      announce('Starting vending machine.');
      await playTransition(config.powerOn, true);
    };

    const openKeypad = () => {
      if (root.dataset.power !== 'on' || root.dataset.busy === 'true') return;
      resetMysteryEffect(false);
      cancelScreenIdleEffect();
      root.dataset.busy = 'true';
      closeup.hidden = true;
      closeup.classList.remove('is-idle');
      closeup.classList.remove('has-product');
      idleImage.src = config.keypadIdle;
      pressedImage.classList.remove('is-visible');
      pressedImage.removeAttribute('src');
      productLink.classList.remove('is-visible');
      productLink.removeAttribute('href');
      productSelectionToken += 1;
      productImageOne.removeAttribute('src');
      productImageTwo.removeAttribute('src');
      productImageShellOne.hidden = true;
      productImageShellTwo.hidden = true;
      productImageShellOne.style.removeProperty('--u20-vending-product-glitch-image');
      productImageShellTwo.style.removeProperty('--u20-vending-product-glitch-image');
      stopProductGlitch();
      selectedIndex = -1;
      syncProductTransitionRoute();
      announce('Select a product from one to twelve.');
      if (REDUCED_QUERY.matches) {
        finishKeypadTransition();
        return;
      }
      root.classList.add('is-keypad-pushing');
      keypadPushTimer = later(() => {
        keypadPushTimer = 0;
        closeup.hidden = false;
        root.classList.remove('is-keypad-pushing');
        root.classList.add('is-keypad-transitioning');
        keypadTransitionTimer = later(() => {
          keypadTransitionTimer = 0;
          finishKeypadTransition();
        }, 840);
      }, 540);
    };

    const showPressedState = (url) => {
      pressedImage.src = url;
      pressedImage.classList.add('is-visible');
      later(() => pressedImage.classList.remove('is-visible'), 170);
    };

    const lightKey = (button) => {
      button.classList.remove('is-pressing');
      void button.offsetWidth;
      button.classList.add('is-pressing');
      later(() => button.classList.remove('is-pressing'), 280);
    };

    const selectProduct = async (index) => {
      const product = products[index];
      const pressedUrl = config.keys[index];
      if (!product || !pressedUrl || root.dataset.busy === 'true') return;

      const images = [product.image1, product.image2].filter(Boolean);
      if (!images.length) return;

      resetScreenIdleEffect();
      const selectionToken = ++productSelectionToken;
      selectedIndex = index;
      closeup.classList.add('has-product');
      showPressedState(pressedUrl);
      idleImage.src = config.keypadProduct;
      stopProductGlitch();
      productImageOne.removeAttribute('src');
      productImageTwo.removeAttribute('src');
      productImageShellOne.hidden = true;
      productImageShellTwo.hidden = true;
      productImageShellOne.style.removeProperty('--u20-vending-product-glitch-image');
      productImageShellTwo.style.removeProperty('--u20-vending-product-glitch-image');
      clearTopImageFit();
      productTitle.textContent = product.title;
      productImages.classList.toggle('is-bottoms', isBottomProduct(product));
      productImages.classList.toggle('is-tops', isTopProduct(product));
      productPrice.textContent = String(product.price || '')
        .replace(/[¥￥$＄]\s*/g, '')
        .trim();
      productLink.href = product.url;
      productLink.setAttribute('aria-label', `View ${product.title}`);
      syncProductTransitionRoute();
      productLink.classList.add('is-visible');
      prefetchProductPage(product.url);
      announce(`Product ${index + 1}: ${product.title}, ${product.price}. Tap the screen to view product.`);

      const loadedImages = (await Promise.all(
        images.map((url) => loadProductImage(url, 'high'))
      )).filter(Boolean);

      if (selectionToken !== productSelectionToken || selectedIndex !== index) return;

      if (!loadedImages.length) {
        announce(`Product ${index + 1}: ${product.title}. Product image is temporarily unavailable. Tap the screen to view product.`);
        return;
      }

      if (productImages.classList.contains('is-tops')) {
        activeTopArtwork = await Promise.all(loadedImages.map(measureArtworkBounds));
        if (selectionToken !== productSelectionToken || selectedIndex !== index) return;
      }

      const applyProductImage = (image, shell, url) => {
        image.src = url;
        image.alt = product.title;
        shell.hidden = false;
        shell.style.setProperty(
          '--u20-vending-product-glitch-image',
          `url("${String(url).replace(/"/g, '\\"')}")`
        );
      };

      applyProductImage(productImageOne, productImageShellOne, loadedImages[0]);
      if (loadedImages[1]) {
        applyProductImage(productImageTwo, productImageShellTwo, loadedImages[1]);
        productImages.classList.remove('is-single');
      } else {
        productImageTwo.removeAttribute('src');
        productImageShellTwo.hidden = true;
        productImages.classList.add('is-single');
      }
      window.requestAnimationFrame(fitActiveTopImages);
      triggerProductGlitch();
      preloadNextProducts(index, 2);
    };

    const powerOff = async () => {
      if (root.dataset.busy === 'true' && !purikuraActive) return;
      cancelPendingPowerOff();
      const generation = exitGeneration;
      if (purikuraActive) closePurikura();
      releaseActiveVendingControl();
      cancelKeypadTransition();
      resetMysteryEffect(false);
      cancelScreenIdleEffect();
      productSelectionToken += 1;
      stopProductGlitch();
      root.classList.add('is-powering-off');
      root.dataset.busy = 'true';
      showPressedState(config.powerPressed);
      announce('Powering off vending machine.');
      powerOffDelayTimer = later(async () => {
        powerOffDelayTimer = 0;
        if (generation !== exitGeneration) return;
        try {
          flash.classList.add('is-active');
          closeup.hidden = true;
          panorama.hidden = false;
          productLink.classList.remove('is-visible');
          selectedIndex = -1;
          await playTransition(config.powerOff, false);
        } catch (error) {
          // The finalizer below owns recovery even if a browser media method throws.
        } finally {
          if (generation !== exitGeneration || signal.aborted) return;
          root.classList.remove('is-keypad-pushing', 'is-keypad-transitioning', 'is-powering-off');
          closeup.hidden = true;
          panorama.hidden = false;
          root.dataset.busy = 'false';
          later(() => flash.classList.remove('is-active'), 160);
          schedulePageScrollRecovery('vending-power-off');
        }
      }, 150);
    };

    const forceEasterHandoffOff = () => {
      cancelPendingPowerOff();
      root.dataset.u20EasterPowerOverride = 'off';
      if (activeTransitionCancel) activeTransitionCancel();
      activeTransitionCancel = null;
      cancelKeypadTransition();
      releaseActiveVendingControl();
      resetMysteryEffect(false);
      cancelScreenIdleEffect();
      stopProductGlitch();
      clearSelectedProduct();
      if (purikuraActive) closePurikura();
      root.classList.remove('is-keypad-pushing', 'is-keypad-transitioning', 'is-powering-off');
      closeup.hidden = true;
      closeup.classList.remove('is-idle', 'has-product');
      panorama.hidden = false;
      idleImage.src = config.keypadIdle;
      pressedImage.classList.remove('is-visible');
      pressedImage.removeAttribute('src');
      flash.classList.remove('is-active');
      try { video.pause(); } catch (error) {}
      video.classList.remove('is-active');
      video.removeAttribute('src');
      video.load();
      activeVideo = false;
      setPower(false);
      setSessionPower(false);
      root.dataset.u20EasterHandoff = 'complete';
      schedulePageScrollRecovery('capsule-video-vending-handoff');
    };

    powerOnButton.addEventListener('click', powerOn, {signal});
    openKeypadButton.addEventListener('click', openKeypad, {signal});
    root.querySelector('[data-u20-vending-power-off]').addEventListener('click', powerOff, {signal});
    root.addEventListener(EASTER_HANDOFF_EVENT, forceEasterHandoffOff, {signal});
    purikuraOpen.addEventListener('click', openPurikura, {signal});
    purikuraClose.addEventListener('click', closePurikura, {signal});
    purikuraOrientationButton.addEventListener('click', () => {
      if (!purikuraActive || purikuraOriginalPixels || purikuraSnap.disabled) return;
      setPurikuraOrientation(purikuraOrientation === 'portrait' ? 'landscape' : 'portrait');
      announce(purikuraOrientation === 'portrait' ? 'Portrait selfie mode.' : 'Landscape selfie mode.');
    }, {signal});
    purikuraSnap.addEventListener('click', beginPurikuraCountdown, {signal});
    purikuraRetake.addEventListener('click', async () => {
      if (!purikuraActive) return;
      purikuraOriginalPixels = null;
      purikuraLuminance = null;
      purikuraHighlightIntegral = null;
      purikuraCanvas.hidden = true;
      purikuraVideo.hidden = false;
      purikuraResultControls.hidden = true;
      purikuraLiveControls.hidden = false;
      purikuraOrientationButton.disabled = false;
      await startPurikuraCamera();
    }, {signal});
    purikuraStrength.addEventListener('input', () => {
      if (!purikuraOriginalPixels) return;
      if (purikuraFilterTimer) {
        window.clearTimeout(purikuraFilterTimer);
        timers.delete(purikuraFilterTimer);
      }
      purikuraFilterTimer = later(() => {
        purikuraFilterTimer = 0;
        renderPurikuraFilter();
      }, 70);
    }, {signal});
    purikuraSave.addEventListener('click', savePurikura, {signal});
    root.querySelectorAll('[data-u20-vending-key]').forEach((button) => {
      const index = Number(button.dataset.u20VendingKey) - 1;
      if (!products[index]) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }
      button.addEventListener('pointerdown', () => {
        preloadProductAt(index, 'high').catch(() => {});
      }, {signal, passive: true});
      button.addEventListener('click', () => {
        if (root.dataset.busy === 'true' || mysteryActive) return;
        lightKey(button);
        if (recordMysteryKey(index + 1)) {
          showPressedState(config.keys[index]);
          triggerMysteryEffect();
          return;
        }
        selectProduct(index);
      }, {signal});
    });

    closeup.addEventListener('pointerdown', resetScreenIdleEffect, {signal, passive: true});
    closeup.addEventListener('keydown', resetScreenIdleEffect, {signal});

    productImageOne.addEventListener('error', () => {
      productImageOne.removeAttribute('src');
      productImageShellOne.style.removeProperty('--u20-vending-product-glitch-image');
    }, {signal});

    productImageTwo.addEventListener('error', () => {
      productImageTwo.removeAttribute('src');
      productImageShellTwo.hidden = true;
      productImageShellTwo.style.removeProperty('--u20-vending-product-glitch-image');
      productImages.classList.add('is-single');
    }, {signal});

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        closePurikura();
        stopProductGlitch();
        cancelScreenIdleEffect();
        if (mysteryActive) resetMysteryEffect(true);
      } else if (!closeup.hidden && visible) {
        scheduleScreenIdleEffect();
      }
      if (document.hidden && (keypadPushTimer || keypadTransitionTimer)) finishKeypadTransition();
      if (!activeVideo) return;
      if (document.hidden) {
        video.pause();
      } else if (visible) {
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }
    }, {signal});

    window.addEventListener('pageshow', (event) => {
      syncProductTransitionRoute();
      if (event.persisted) schedulePageScrollRecovery('vending-bfcache-return');
    }, {signal});

    window.addEventListener('pagehide', closePurikura, {signal});

    const observer = typeof window.IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
        visible = Boolean(entries[0] && entries[0].isIntersecting);
        if (!visible) {
          closePurikura();
          stopProductGlitch();
          cancelScreenIdleEffect();
          if (mysteryActive) resetMysteryEffect(true);
        } else if (!document.hidden && !closeup.hidden) {
          scheduleScreenIdleEffect();
        }
        if (!visible && (keypadPushTimer || keypadTransitionTimer)) finishKeypadTransition();
        if (!activeVideo) return;
        if (!visible) {
          video.pause();
        } else if (!document.hidden) {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        }
      }, {rootMargin: '15% 0px', threshold: 0.01})
      : null;
    if (observer) observer.observe(stage);

    destroy = () => {
      if (signal.aborted) return;
      productPreloadStopped = true;
      productImageCache.forEach((record) => {
        if (!record.image) return;
        record.image.onload = null;
        record.image.onerror = null;
      });
      productImageCache.clear();
      if (activeTransitionCancel) activeTransitionCancel();
      activeTransitionCancel = null;
      cancelPendingPowerOff();
      if (observer) observer.disconnect();
      closePurikura();
      cancelScreenIdleEffect();
      resetMysteryEffect(false);
      stopProductGlitch();
      root.classList.remove('is-keypad-pushing');
      root.classList.remove('is-keypad-transitioning');
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      try { video.pause(); } catch (error) {}
      video.classList.remove('is-active');
      video.removeAttribute('src');
      video.load();
      activeVideo = false;
      controller.abort();
      root.dataset.u20VendingInitialized = 'false';
      instances.delete(root);
      releasePending();
    };

    const powered = initialPowered;
    setPower(powered);
    closeup.hidden = true;
    panorama.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    section.style.setProperty(
      '--u20-vending-wallpaper',
      `url("${String(config.background).replace(/"/g, '\\"')}")`
    );
    section.style.setProperty(
      '--u20-vending-transition-scene',
      `url("${String(config.machineOn).replace(/"/g, '\\"')}"), url("${String(config.background).replace(/"/g, '\\"')}")`
    );
    section.classList.add('u20-vending-ready');
    releasePending();

    if (pendingEntryIntent === 'power' && !powered) {
      later(powerOn, 0);
    } else if (pendingEntryIntent === 'keypad' && powered) {
      later(openKeypad, 0);
    }

    const tideRuntime = window.__u20MobileApparelTideV458365;
    if (tideRuntime && typeof tideRuntime.refresh === 'function') {
      tideRuntime.refresh();
    }
  };

  const initAll = (scope = document) => {
    if (!MOBILE_QUERY.matches) return;
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      init(root).catch(() => {
        const destroy = instances.get(root);
        if (destroy) destroy();
        root.dataset.u20VendingInitialized = 'false';
        const section = root.closest('[data-u20-apparel-boundary]');
        if (section) section.classList.remove('u20-vending-pending');
      });
    });
  };

  const destroyAll = () => {
    Array.from(instances.values()).forEach((destroy) => destroy());
    instances.clear();
  };

  const onBreakpointChange = (event) => {
    if (event.matches) {
      initAll();
      return;
    }
    destroyAll();
  };

  const onSectionLoad = (event) => initAll(event.target);
  window[RUNTIME_KEY] = {initAll, destroyAll};
  initAll();
  document.addEventListener('shopify:section:load', onSectionLoad);
  if (typeof MOBILE_QUERY.addEventListener === 'function') {
    MOBILE_QUERY.addEventListener('change', onBreakpointChange);
  } else {
    MOBILE_QUERY.addListener(onBreakpointChange);
  }
})();
